"use client";

import { useActionState, useState, useRef } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useEffect } from "react";
import AuthInput from "@/app/ui/atomic/content/AuthInput";
import FadeIn from "@/app/ui/atomic/animations/FadeIn";
import PasskeyButton from "@/app/ui/atomic/content/PasskeyButton";
import SocialLoginButtons from "./SocialLoginButtons";
import { signInAction, SignInState } from "./sign-in/actions";
import { signUpAction, SignUpState } from "./sign-up/actions";
import { isClientSafeRedirect } from "@/lib/auth/safeRedirect";

interface AuthFormProps {
  initialMode?: "signin" | "signup";
  redirectUrl?: string;
}

export default function AuthForm({ initialMode = "signin", redirectUrl }: AuthFormProps) {
  const router = useRouter();
  const [mode, setMode] = useState<"signin" | "signup">(initialMode);
  const [passkeyError, setPasskeyError] = useState<string | null>(null);

  const signInInitialState: SignInState = { success: false };
  const signUpInitialState: SignUpState = { success: false };

  const [signInState, signInFormAction, signInPending] = useActionState(
    async (prevState: SignInState, formData: FormData) => {
      if (redirectUrl) {
        formData.set("redirect", redirectUrl);
      }
      return signInAction(prevState, formData);
    },
    signInInitialState
  );

  const [signUpState, signUpFormAction, signUpPending] = useActionState(
    signUpAction,
    signUpInitialState
  );

  useEffect(() => {
    if (signInState.success && signInState.redirectTo) {
      const target = isClientSafeRedirect(signInState.redirectTo) ? signInState.redirectTo : "/account";
      if (target.startsWith("http") || target.startsWith("/api/")) {
        window.location.replace(target);
      } else {
        router.replace(target);
        router.refresh();
      }
    }
  }, [signInState.success, signInState.redirectTo, router]);

  useEffect(() => {
    if (signUpState.success) {
      setMode("signin"); // eslint-disable-line react-hooks/set-state-in-effect -- state transition on registration success
      const registeredUrl = redirectUrl
        ? `/auth?registered=true&redirect=${encodeURIComponent(redirectUrl)}`
        : "/auth?registered=true";
      router.push(registeredUrl);
    }
  }, [signUpState.success, router, redirectUrl]);

  const toggleMode = () => {
    setMode(mode === "signin" ? "signup" : "signin");
    setPasskeyError(null);
  };

  const currentError = mode === "signin" 
    ? (signInState.error || passkeyError) 
    : signUpState.error;

  const isPending = mode === "signin" ? signInPending : signUpPending;

  const errorRef = useRef<HTMLDivElement | null>(null);
  const prevErrorRef = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    if (currentError && currentError !== prevErrorRef.current && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
      errorRef.current.focus({ preventScroll: true });
    }
    prevErrorRef.current = currentError;
  }, [currentError]);

  return (
    <FadeIn delay={50}>
      <div className="w-full max-w-md mx-auto px-5">
        <div className="bg-(--bg-card) rounded-2xl shadow-xl overflow-hidden border border-(--border-color)">
          <div className="flex border-b border-(--border-color)">
            <button
              type="button"
              onClick={() => setMode("signin")}
              className={`flex-1 py-4 text-sm font-semibold transition-colors ${
                mode === "signin"
                  ? "bg-(--purple)/10 text-(--purple-accent) border-b-2 border-(--purple-accent)"
                  : "text-(--text-muted) hover:text-(--text-primary) hover:bg-(--purple)/5"
              }`}
            >
              Sign In
            </button>
            <button
              type="button"
              onClick={() => setMode("signup")}
              className={`flex-1 py-4 text-sm font-semibold transition-colors ${
                mode === "signup"
                  ? "bg-(--purple)/10 text-(--purple-accent) border-b-2 border-(--purple-accent)"
                  : "text-(--text-muted) hover:text-(--text-primary) hover:bg-(--purple)/5"
              }`}
            >
              Create Account
            </button>
          </div>

          <div className="p-6 md:p-8">
            {currentError && (
              <div ref={errorRef} tabIndex={-1} role="alert" className="mb-4 p-3 rounded-lg bg-(--status-danger-bg) text-(--status-danger-text) text-sm text-center">
                {currentError}
              </div>
            )}

            {mode === "signin" ? (
              <div className="space-y-4">
                <PasskeyButton
                  onSuccess={() => {
                    const target = (redirectUrl && isClientSafeRedirect(redirectUrl)) ? redirectUrl : "/account";
                    if (target.startsWith("http") || target.startsWith("/api/")) {
                      window.location.replace(target);
                    } else {
                      router.replace(target);
                      router.refresh();
                    }
                  }}
                  onError={(error) => setPasskeyError(error)}
                />

                <SocialLoginButtons redirectUrl={redirectUrl} mode="signin" />

                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-(--border-color)" />
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-(--bg-card) text-(--text-muted)">or continue with email</span>
                  </div>
                </div>

                <form action={signInFormAction} className="space-y-4">
                  <AuthInput
                    name="email"
                    type="email"
                    label="Email:"
                    labelClassName="text-(--purple-accent)"
                    placeholder="Enter your email"
                    required
                    maxLength={100}
                    autoComplete="email"
                    errors={signInState.fieldErrors?.email}
                  />

                  <AuthInput
                    name="password"
                    type="password"
                    label="Password:"
                    labelClassName="text-(--purple-accent)"
                    placeholder="Enter your password"
                    required
                    minLength={12}
                    maxLength={128}
                    autoComplete="current-password"
                    errors={signInState.fieldErrors?.password}
                  />

                  <div className="text-center">
                    <Link
                      href="/auth/forgot-password"
                      className="text-sm text-(--purple-accent) hover:text-(--brand) underline transition-colors"
                    >
                      Forgot password?
                    </Link>
                  </div>

                  <button
                    type="submit"
                    disabled={isPending}
                    className="w-full py-3 px-4 bg-gradient-to-r from-(--brand) to-(--gold) text-(--dark-purple) font-bold rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isPending ? "Signing in..." : "Sign In"}
                  </button>
                </form>
              </div>
            ) : (
              <div className="space-y-4">
                <SocialLoginButtons redirectUrl={redirectUrl} mode="signup" />

                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-(--border-color)" />
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-(--bg-card) text-(--text-muted)">or create with email</span>
                  </div>
                </div>

              <form action={signUpFormAction} className="space-y-4">
                <AuthInput
                  name="name"
                  type="text"
                  label="Name:"
                  labelClassName="text-(--purple-accent)"
                  placeholder="Enter your name"
                  required
                  maxLength={100}
                  autoComplete="name"
                  filterInput={(v: string) => v.replace(/[^a-zA-ZÀ-ÿ\s'\-\.]/g, "")}
                  errors={signUpState.fieldErrors?.name}
                />

                <AuthInput
                  name="email"
                  type="email"
                  label="Email:"
                  labelClassName="text-(--purple-accent)"
                  placeholder="Enter your email"
                  required
                  maxLength={100}
                  autoComplete="email"
                  errors={signUpState.fieldErrors?.email}
                />

                <AuthInput
                  name="password"
                  type="password"
                  label="Password:"
                  labelClassName="text-(--purple-accent)"
                  placeholder="Enter password (12+ characters)"
                  required
                  minLength={12}
                  maxLength={128}
                  autoComplete="new-password"
                  errors={signUpState.fieldErrors?.password}
                />

                <AuthInput
                  name="passwordConfirm"
                  type="password"
                  label="Confirm Password:"
                  labelClassName="text-(--purple-accent)"
                  placeholder="Confirm your password"
                  required
                  minLength={12}
                  maxLength={128}
                  autoComplete="new-password"
                  errors={signUpState.fieldErrors?.passwordConfirm}
                />

                <p className="text-xs text-(--text-muted) text-center">
                  Password must be 12-128 characters
                </p>

                <button
                  type="submit"
                  disabled={isPending}
                  className="w-full py-3 px-4 bg-gradient-to-r from-(--brand) to-(--gold) text-(--dark-purple) font-bold rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isPending ? "Creating Account..." : "Create Account"}
                </button>
              </form>
              </div>
            )}

            <p className="text-center text-sm text-(--text-muted) mt-6">
              {mode === "signin" ? (
                <>
                  Don&apos;t have an account?{" "}
                  <button
                    type="button"
                    onClick={toggleMode}
                    className="text-(--purple-accent) hover:text-(--brand) underline transition-colors"
                  >
                    Create one
                  </button>
                </>
              ) : (
                <>
                  Already have an account?{" "}
                  <button
                    type="button"
                    onClick={toggleMode}
                    className="text-(--purple-accent) hover:text-(--brand) underline transition-colors"
                  >
                    Sign in
                  </button>
                </>
              )}
            </p>
          </div>
        </div>
      </div>
    </FadeIn>
  );
}
